import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../lib/auth';
import { TERMS_VERSION } from '../../lib/config/termsVersion';
import {
  acceptInvite,
  InviteApiError,
  INVITE_ACCEPTANCE_STORAGE_KEY,
  type InviteLookup,
  type InviteErrorCode,
  type InviteOAuthAcceptancePayload,
} from '../../lib/services/invitesApi';
import { LegalAcceptanceCheckboxes } from '../legal/LegalAcceptanceCheckboxes';
import { InviteSocialButtons } from './InviteSocialButtons';
import { InviteCredentialFields } from './InviteCredentialFields';

interface InviteAcceptFormProps {
  token: string;
  invite: InviteLookup;
}

// Messaggi mappati per gli errori noti di /api/invites/accept*. Gli altri codici
// (already_invited, invalid_role, invalid_email, terms_required, invalid_name,
// weak_password, email_mismatch, unknown) ricadono sul fallback generico.
// Esportato: riusato anche da InviteAcceptPage per il banner ?error=already_registered
// del ramo OAuth (stesso messaggio, stessa fonte di verità).
export const ALREADY_REGISTERED_MESSAGE = 'Questa email ha già un account Flusia. Accedi normalmente.';

const ACCEPT_ERROR_MESSAGES: Partial<Record<InviteErrorCode, string>> = {
  seats_full: "L'organizzazione ha esaurito i posti del piano. Contatta l'amministratore.",
  already_registered: ALREADY_REGISTERED_MESSAGE,
  invalid_invite: 'Invito non valido o scaduto. Chiedi un nuovo invito all\'amministratore.',
};
const GENERIC_ACCEPT_ERROR = 'Si è verificato un errore durante l\'accettazione dell\'invito. Riprova più tardi.';

function mapAcceptError(err: unknown): string {
  if (err instanceof InviteApiError) {
    return ACCEPT_ERROR_MESSAGES[err.code] || GENERIC_ACCEPT_ERROR;
  }
  return GENERIC_ACCEPT_ERROR;
}

export function InviteAcceptForm({ token, invite }: InviteAcceptFormProps) {
  const navigate = useNavigate();
  const { signInWithOAuth } = useAuth();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [vexatiousAccepted, setVexatiousAccepted] = useState(false);
  const [termsError, setTermsError] = useState<string | null>(null);
  const [nameErrors, setNameErrors] = useState<{ firstName?: string; lastName?: string }>({});
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [oauthLoading, setOauthLoading] = useState<'google' | 'azure' | null>(null);

  // Gate social via env VITE_OAUTH_* (go-live 16 lug 2026, stesso pattern di RegisterPage/Login).
  const showGoogle = import.meta.env.VITE_OAUTH_GOOGLE_ENABLED === 'true';
  const showMicrosoft = import.meta.env.VITE_OAUTH_MICROSOFT_ENABLED === 'true';

  const validateNames = (): boolean => {
    const errs: { firstName?: string; lastName?: string } = {};
    if (!firstName.trim()) errs.firstName = 'Nome richiesto';
    if (!lastName.trim()) errs.lastName = 'Cognome richiesto';
    setNameErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // Checkbox esplicita + approvazione clausole vessatorie: obbligatorie per
  // entrambi i percorsi (password e social), stessa regola di RegisterPage.
  const validateTerms = (): boolean => {
    if (!termsAccepted) {
      setTermsError('Devi accettare i Termini e Condizioni per procedere');
      return false;
    }
    if (!vexatiousAccepted) {
      setTermsError('Devi approvare specificamente le clausole indicate per procedere');
      return false;
    }
    setTermsError(null);
    return true;
  };

  const validatePasswordFields = (): boolean => {
    if (password.length < 8) {
      setPasswordError('La password deve contenere almeno 8 caratteri');
      return false;
    }
    if (password !== confirmPassword) {
      setPasswordError('Le password non corrispondono');
      return false;
    }
    setPasswordError(null);
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitError(null);
    const namesOk = validateNames();
    const passwordOk = validatePasswordFields();
    const termsOk = validateTerms();
    if (!namesOk || !passwordOk || !termsOk) return;

    setIsSubmitting(true);
    try {
      await acceptInvite({
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        terms: { version: TERMS_VERSION, vexatiousAccepted: true },
      });

      const { error: signInError } = await supabase.auth.signInWithPassword({
        email: invite.email,
        password,
      });
      if (signInError) throw signInError;

      // Mini-onboarding invitato: foto profilo + scelta moduli, poi dashboard
      navigate('/welcome/profile', { replace: true });
    } catch (err) {
      console.error('[InviteAcceptForm] accept/signIn error:', err);
      setSubmitError(mapAcceptError(err));
    } finally {
      setIsSubmitting(false);
    }
  };

  // Percorso social: valida nome+cognome+checkbox PRIMA del redirect (non possiamo
  // più farlo dopo, torniamo su una pagina diversa). Persiste i dati in sessionStorage,
  // li legge OAuthCallback al ritorno per chiamare acceptInvite via /accept-oauth.
  const handleSocial = async (provider: 'google' | 'azure') => {
    setSubmitError(null);
    const namesOk = validateNames();
    const termsOk = validateTerms();
    if (!namesOk || !termsOk) return;

    try {
      const acceptancePayload: InviteOAuthAcceptancePayload = {
        token,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        terms: { version: TERMS_VERSION, vexatiousAccepted: true },
      };
      sessionStorage.setItem(INVITE_ACCEPTANCE_STORAGE_KEY, JSON.stringify(acceptancePayload));
    } catch (storageErr) {
      console.warn('[InviteAcceptForm] Unable to persist invite acceptance for OAuth flow:', storageErr);
    }

    setOauthLoading(provider);
    const result = await signInWithOAuth(provider);
    if (result.error) {
      sessionStorage.removeItem(INVITE_ACCEPTANCE_STORAGE_KEY);
      setSubmitError(result.error.message || `Errore con ${provider === 'google' ? 'Google' : 'Microsoft'}. Riprova.`);
      setOauthLoading(null);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <InviteCredentialFields
        firstName={firstName}
        lastName={lastName}
        onFirstNameChange={setFirstName}
        onLastNameChange={setLastName}
        nameErrors={nameErrors}
        password={password}
        confirmPassword={confirmPassword}
        onPasswordChange={setPassword}
        onConfirmPasswordChange={setConfirmPassword}
        passwordError={passwordError}
      />

      <LegalAcceptanceCheckboxes
        termsAccepted={termsAccepted}
        vexatiousAccepted={vexatiousAccepted}
        onTermsChange={(checked) => { setTermsAccepted(checked); if (checked) setTermsError(null); }}
        onVexatiousChange={(checked) => { setVexatiousAccepted(checked); if (checked) setTermsError(null); }}
        error={termsError}
      />

      {submitError && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3">
          <p className="text-xs text-red-700">{submitError}</p>
        </div>
      )}

      <button
        type="submit"
        disabled={isSubmitting || !!oauthLoading}
        className="notion-register-btn w-full"
      >
        {isSubmitting ? (
          <>
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
            <span>Accettazione in corso...</span>
          </>
        ) : (
          <>
            <span>Accetta e crea account</span>
            <ArrowRight className="w-4 h-4" />
          </>
        )}
      </button>

      <InviteSocialButtons
        showGoogle={showGoogle}
        showMicrosoft={showMicrosoft}
        oauthLoading={oauthLoading}
        disabled={isSubmitting}
        onSelect={handleSocial}
      />
    </form>
  );
}

export default InviteAcceptForm;
